import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { LuCalendarClock } from "react-icons/lu";
import { AddButton } from "../../general_components/content_management/reusableItems/buttons";
import { addNotification, addPopup } from "../../supplementary_components/store";
import { CustomAddForm } from "../../general_components/forms/CustomAddForm";
import { API_ROOT, sendRequest } from "../../supplementary_components/requests";
import { ConfirmationDialogue } from "../../general_components/forms/ConfirmationDialogue";
import { ListView } from "../../general_components/content_management/list_view/listView"
// import { generateColumn } from "../../supplementary_components/itemEditMethods";
import { dateFormatter } from "../../supplementary_components/supplementaryMethods";
import styles from "./trailer.module.scss";
const COMPANY_TIMEZONE = () => localStorage.getItem("defaultTimezone") || "America/Chicago";

/** Formats ISO LocalDateTime to "03/10 04:00 PM" in company timezone */
function formatDateTimeForDisplay(isoStr, zone, includeYear) {
    const dt = DateTime.fromISO(isoStr, { zone }).setLocale("en-US");
    return dt.isValid ? dt.toFormat(includeYear ? "MM/dd/yy hh:mm a" : "MM/dd hh:mm a") : "-";
}

/** Returns "X day(s)" passed since start using server currentTime */
function getUsedForDisplay(startDateTime, currentTimeUtc, zone) {
    const now = DateTime.fromISO(currentTimeUtc, { zone: "utc" });
    const start = DateTime.fromISO(startDateTime, { zone });
    if (!start.isValid) return "-";
    if (now < start) return "-";
    const diff = now.diff(start);
    const totalMinutes = diff.as("minutes");
    const totalHours = diff.as("hours");
    const totalDays = diff.as("days");
    if (totalMinutes < 60) {
        const m = Math.floor(totalMinutes);
        return `${m} minute${m === 1 ? "" : "s"}`;
    }
    if (totalHours < 24) {
        const h = Math.floor(totalHours);
        return `${h} hour${h === 1 ? "" : "s"}`;
    }
    const d = Math.floor(totalDays);
    return `${d} day${d === 1 ? "" : "s"}`;
}

/** Parses time-left string to total hours (for conditional formatting) */
function parseTimeLeftToHours(str) {
    if (!str || str === "Expired" || str === "-") return 0;
    let totalHours = 0;
    const dayMatch = str.match(/(\d+)\s*day/);
    if (dayMatch) totalHours += parseInt(dayMatch[1], 10) * 24;
    const hourMatch = str.match(/(\d+)\s*hour/);
    if (hourMatch) totalHours += parseInt(hourMatch[1], 10);
    const minuteMatch = str.match(/(\d+)\s*minute/);
    if (minuteMatch) totalHours += parseInt(minuteMatch[1], 10) / 60;
    return totalHours;
}

/** Returns "X day(s) Y hour(s) left" or "Expired" using server currentTime */
function getTimeLeftDisplay(endDateTime, currentTimeUtc, zone) {
    const now = DateTime.fromISO(currentTimeUtc, { zone: "utc" });
    const end = DateTime.fromISO(endDateTime, { zone });
    if (!end.isValid) return "-";
    if (end <= now) return "Expired";
    const diff = end.diff(now, ["days", "hours"]);
    const d = Math.floor(diff.days);
    const h = Math.floor(diff.hours % 24);
    if (d >= 2) return `${d} days`;
    if (d > 0 && h > 0) return `${d} day${d === 1 ? "" : "s"} ${h} hour${h === 1 ? "" : "s"}`;
    if (d > 0) return `${d} day${d === 1 ? "" : "s"}`;
    return `${h} hour${h === 1 ? "" : "s"}`;
}

function transformSingleReservation(r, currentTime, zone) {
    return {
        ...r,
        startDateTimeFormatted: formatDateTimeForDisplay(r.startDateTime, zone, true),
        endDateTimeFormatted: formatDateTimeForDisplay(r.endDateTime, zone, true),
        usedForDisplay: currentTime ? getUsedForDisplay(r.startDateTime, currentTime, zone) : "-",
        timeLeftDisplay: currentTime ? getTimeLeftDisplay(r.endDateTime, currentTime, zone) : "-",
    };
}

function transformReservationsForDisplay(dataset) {
    const zone = COMPANY_TIMEZONE();
    const currentTime = dataset?.currentTime;
    return (dataset?.reservations || []).map((r) => transformSingleReservation(r, currentTime, zone));
}

/** Parses "MM/DD/YY HH:MM AM/PM" to Date */
function parseDateWithTime(str) {
    const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    const [, mm, dd, yy, h, min, ampm] = match;
    const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
    const month = parseInt(mm, 10) - 1;
    const day = parseInt(dd, 10);
    let hour = parseInt(h, 10);
    if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
    return new Date(year, month, day, hour, parseInt(min, 10), 0, 0);
}

/** Formats Date to ISO string for Java LocalDateTime (yyyy-MM-ddTHH:mm:ss) */
function toLocalDateTime(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}`;
}

function buildTrailerReservationBody(rawBody) {
    const start = parseDateWithTime(rawBody.startDateTime);
    let end = new Date(start);
    if(rawBody.durationInDays){
        const durationDays = parseInt(rawBody.durationInDays, 10) || null;
        if (!start) return null;
        delete rawBody.durationInDays;
        end.setDate(end.getDate() + durationDays);
    }else if(rawBody.endDateTime){
        end = parseDateWithTime(rawBody.endDateTime);
    }else{
        return null
    }
    
    return {
        ...rawBody,
        startDateTime: toLocalDateTime(start),
        endDateTime: toLocalDateTime(end),
    };
}
const TrailerReservaitonListConf = {
	columnProportions: "0.6fr 1.6fr 0.8fr 0.7fr 0.9fr 0.8fr 0.8fr 1.7fr 3rem",
	dataTitle: "Reservation",
	dataTitlePlural:"Reservations",
	category: 'trailers',
	editAccessLevel: { key: 'TRAILER_MANAGEMENT' },
	columns: [
		{
			label: "Trailer",
			columnName: "trailer",
			sortable: true,
            dataValueRequired: true,
            valueGenerator: (data)=>{
                if(data?.trailer?.trailerNumber){
                    return data.trailer.trailerNumber;
                }
                return "-";
            },
		},
		{
			label: "Address",
			columnName: "address",
			sortable: true,
		},
		{
			label: "Start",
			columnName: "startDateTime",
			sortable: true,
            valueGenerator: (data) => data.startDateTimeFormatted.replace(/\/\d{2,4}(?= )/, "") ?? "-",
            transformerForSorting: (data) => {
                return DateTime.fromFormat(data.startDateTimeFormatted, "MM/dd/yy hh:mm a")
            },
		},
        {
			label: "Active For",
			columnName: "usedForDisplay",
			sortable: true,
            valueGenerator: (data) => data.usedForDisplay ?? "-",
            transformerForSorting: (data) => {
                return parseTimeLeftToHours(data.usedForDisplay)
            },
		},
        {
			label: "Expires",
			columnName: "endDateTime",
			sortable: true,
            valueGenerator: (data) => data.endDateTimeFormatted.replace(/\/\d{2,4}(?= )/, "") ?? "-",
            transformerForSorting: (data) => {
                return DateTime.fromFormat(data.endDateTimeFormatted, "MM/dd/yy hh:mm a")
            },
		},
        {
			label: "Time Left",
			columnName: "timeLeftDisplay",
            valueGenerator: (data) => data.timeLeftDisplay ?? "-",
            sortable: true,
            conditionalFormatting: (value) => {
                // value = "1 day 2 hours";
                const hoursLeft = parseTimeLeftToHours(value);
                return hoursLeft < 48
                    ? `& > * {background-color:var(--alert-color); padding: 0.2rem 0.8rem; border-radius: 0.5rem; color: white !important; font-weight: 700;}`
                    : `& > * {}`;
            },
            transformerForSorting: (data) => {
                return parseTimeLeftToHours(data.timeLeftDisplay)
            },
		},
        {
			label: "Reservation Number",
			columnName: "reservationNumber",
		},
        {
			label: "Notes",
			columnName: "notes",
		},
        {
            actionButtons:true,
            notEditableIfValueIs : (val)=>{
                return false;
            },
            onEditAction: (reservation, postSuccessAction)=>{
				const formData = {
					...NewRecordForm,
					columns: NewRecordForm.columns
						.filter(column => column.name !== "durationInDays")
						.map(column => ({
                            
							...column,
							defaultValue: () => column.name === "startDateTime" ? formatDateTimeForDisplay(reservation[column.name], COMPANY_TIMEZONE(), true) : column.dataValueRequired ? column.displayValueGenerator(reservation[column.name]) : reservation[column.name],
                            predefinedDatValue:column.dataValueRequired ? JSON.stringify(reservation[column.name]) : null,
						})),
				};
                const startDateTimeColumn = formData.columns.find((c) => c.name === "startDateTime");
                const endDateTimeColumn = {
                    ...startDateTimeColumn,
                    label: "End Date Time",
                    name: "endDateTime",
                    defaultValue: () => formatDateTimeForDisplay(reservation["endDateTime"], COMPANY_TIMEZONE(), true),
                };
                formData.columns.splice(3, 0, endDateTimeColumn);
                formData.columns.push({
                    hiddenField:true,
                    name:"id",
                    defaultValue:()=>{
                        return reservation.id;
                    }
                });
                
				showTrailerReservationAddForm(formData, "Edit", postSuccessAction);
            },
            onRemoveAction: (site, postSuccessAction)=>{
                addPopup({
                    content:ConfirmationDialogue, 
                    popupData: {
                        confirmationDialogue:true,
                        action:(postResultAction)=>{
                            sendRequest("DELETE", API_ROOT+`trailer-reservations/${site.id}`, null, (res)=>{
                                postSuccessAction(res, true);
                                postResultAction(true);
                                addNotification({message: `Site Was Removed`, type:'success'});
                            }, (e)=>{
                                addNotification({message: e.customError ? e.customError :`Something Went Wrong`, type:'alert'});
                                postResultAction(true);
                            } )
                        },
                        cancelAction:(closePopup)=>{
                            closePopup()
                        },
                        name:"Confirm",
                        formTitle:"Delete Site",
                        submitTitle:"Delete",
                        message:`Are you sure you want to delete "${site.name}"?`
                    }, 
                    postSuccessAction: ()=>{
                        
                    }, 
                    EZCloseAllowed:true
                })
                
               
            }
        }

	]
}
const NewRecordForm = {columns: [
	{
        label: "Trailer Number",
        name: "trailer",
        mandatory: true,
        autoComplete:"off",
        suggestionsEnabled: true,
        suggestionsLink: API_ROOT+'trailers/trailersBySimilarTrailerNumberForCurrentCompanyAndThirdPartyTrailers?pattern=',
        dataValueRequired : true,
        afterMinimumChars:2,
        displayValueGenerator: (val)=>{return `${val["trailerNumber"]}`;},
        displayValueToRawValue: (value)=>{ return value},
    },
    {
        label: "Address",
        autoComplete: "off",
        name: "address",
        mandatory: true,
    },
    {
        label: "Start Date Time",
        name: 'startDateTime',
        mandatory: true,
        autoComplete:"off",
        checks : [
            (value) => {
                const res = dateFormatter(value);
                if(res.date === null){
                    return 'Invalid Input'
                }
                if(res.time === null){
                    return 'Invalid Input'
                }
                return null;
            }
        ],
        onblur:(val)=>{
            const date = dateFormatter(val).date;
            const time = dateFormatter(val).time;
            return date && time ? `${date} ${time}` : val;
        },
        defaultValue:  (val)=>{
            //formatDateTimeForDisplay
            const now = new Date();
            const mm = String(now.getMonth() + 1).padStart(2, '0');
            const dd = String(now.getDate()).padStart(2, '0');
            const yy = String(now.getFullYear()).slice(-2);
            let h = now.getHours();
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12 || 12;
            const min = String(now.getMinutes()).padStart(2, '0');
            return `${mm}/${dd}/${yy} ${String(h).padStart(2, '0')}:${min} ${ampm}`;
        },
    },
    {
        label: "Duration In Days",
        name: "durationInDays",
        filters:[
            (val)=>{
                return val.replace(/[^0-9]/g,"");
            }
        ],
        mandatory: true,
    },
    {
        label: "Reservation Number",
        autoComplete: "off",
        name: "reservationNumber",
    },
    {
        label:"Notes",
        name: 'notes',
        type:'textArea',
        rowCount: 5,
    },
]}
const showTrailerReservationAddForm = (form, buttonLabel, onChange) => {
    addPopup({
        content:CustomAddForm, 
        popupData: {
        action:(event, postResultAction)=>{
            event.preventDefault();
            const target = event.target;
            const body = {}
            for(const clmn of form['columns']){
                let inputField = target.querySelector(`input[name="${clmn.name}"]`);
                if(clmn.dataValueRequired){
                    if(inputField.value === ''){
                        body[clmn.name] = null;
                        continue;
                    }
                    body[clmn.name] = JSON.parse(inputField.getAttribute('data-value'))
                }else{
                    if(!inputField){
                        inputField = target.querySelector(`select[name="${clmn.name}"]`);
                    }
                    if(!inputField){
                        inputField = target.querySelector(`textarea[name="${clmn.name}"]`);
                    }
                    if(inputField && !inputField.disabled){
                        body[clmn.name] = clmn.processBeforeSending ? clmn.processBeforeSending(inputField.value) : inputField.value;
                    }
                }                
            }
            
            const reservationBody = buildTrailerReservationBody(body);
            if (!reservationBody) {
                addNotification({ message: 'Invalid start date/time format', type: 'alert' });
                postResultAction(false);
                return;
            }
            sendRequest('POST', API_ROOT+'trailer-reservations/updateTrailerReservation',null,(res)=>{
                if (onChange) onChange(res);
                postResultAction(true);
            }, ()=>{
                postResultAction(false);
                addNotification({message:`Something went wrong`, type:'alert'});
            }, reservationBody);
        },
            
        name:buttonLabel,
        formTitle:buttonLabel+" Reservation",
        submitTitle:buttonLabel,
        method:"POST",
        columns: form['columns']
        }, 
        EZCloseAllowed:true
    })
}
const ALERT_THRESHOLD_HOURS = 48;

function reservationsHaveExpiringAlert(reservations) {
    if (!Array.isArray(reservations)) return false;
    return reservations.some((r) => {
        if (!r?.timeLeftDisplay || r.timeLeftDisplay === "-") return false;
        return parseTimeLeftToHours(r.timeLeftDisplay) < ALERT_THRESHOLD_HOURS;
    });
}

export const TrailerReservationManagement = ({popupData}) => {
    const [reservations, setReservations] = useState(null);

    useEffect(() => {
        if (typeof popupData?.setReservationAlert !== "function") return;
        if (!Array.isArray(reservations)) return;
        popupData.setReservationAlert(reservationsHaveExpiringAlert(reservations));
    }, [reservations, popupData]);

    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();
        sendRequest(
            "GET",
            API_ROOT + "trailers/reservations",
            controller.signal,
            (res) => {
                if (ignore) return;
                const list = Array.isArray(res) ? res : res?.reservations;
                const ct = Array.isArray(res) ? null : (res?.currentTime ?? null);
                setReservations(
                    Array.isArray(list)
                        ? transformReservationsForDisplay({ reservations: list, currentTime: ct })
                        : []
                );
            },
            (err) => {
                if (ignore || err?.name === "AbortError") return;
                addNotification({ message: "Failed to load reservations", type: "alert" });
                setReservations([]);
            }
        );
        return () => {
            ignore = true;
            controller.abort();
        };
    }, []);

    const onReservationChange = (reservation, remove) => {
        if(remove){
            setReservations((prev) => {
                const idx = prev.findIndex((r) => r.id === reservation.id);
                if (idx >= 0) {
                    const next = [...prev];
                    next.splice(idx, 1);
                    return next;
                }
                return prev;
            });
            return;
        }
        const zone = COMPANY_TIMEZONE();
        const ct = reservation.currentTime;
        const transformed = transformSingleReservation(reservation, ct, zone);
        setReservations((prev) => {
            const next = prev ? [...prev] : [];
            const existingIdx = next.findIndex((r) => r.id === transformed.id);
            if (existingIdx >= 0) next.splice(existingIdx, 1);
            const insertAt = next.findIndex((r) => r.endDateTime > transformed.endDateTime);
            next.splice(insertAt === -1 ? next.length : insertAt, 0, transformed);
            return next;
        });
    };
    

    const reservationCount = Array.isArray(reservations) ? reservations.length : null;

    return <div className={styles.trailerReservationManagementWrapper}>
        <div className={styles.trailerReservationHeader}>
            <h2 className={styles.trailerReservationTitle}>
                <LuCalendarClock />
                Trailer Reservations
                {reservationCount !== null ? (
                    <span className={styles.trailerReservationCount}>{reservationCount}</span>
                ) : null}
            </h2>
            {Array.isArray(reservations) ? (
                <div className={styles.trailerReservationHeaderActions}>
                    <AddButton customSize={"1.8rem"} action={()=>{
                        showTrailerReservationAddForm(NewRecordForm, "Add", onReservationChange);
                    }} />
                </div>
            ) : null}
        </div>
        <div className={styles.trailerReservationBody}>
            <ListView ListConf={TrailerReservaitonListConf} Data={reservations} Error={null} NoFooter={true} RequestReload={onReservationChange} PaginationData={null} filteredData={null} listBeingUpdated={false} selectedCategory={null} noBoxRadius={true} />
        </div>
    </div>
}